import React, { useState } from "react";
import "./App.css";
import { db, isFirebaseConfigured } from "./firebase";
import {
	collection,
	addDoc,
	query,
	where,
	getDocs,
	Timestamp,
} from "firebase/firestore";
import ShootingStars from "./components/ui/shooting-stars";
import { ShootingStars as ShadcnShootingStars } from "./components/ui/shadcn-io/shooting-stars";
import { BackgroundBoxes } from "./components/ui/background-boxes";

// Helper function to get course image path
const getCourseImage = (courseName) => {
	const imageMap = {
		Minecraft: "/images/minecraft.avif",
		Roblox: "/images/roblox.webp",
		Scratch: "/images/scratch.jpg",
		Python: "/images/python.webp",
	};
	return imageMap[courseName] || null;
};

const courses = [
	{
		name: "Minecraft",
		icon: "⛏️",
		description: "Code your own Minecraft mods and adventures",
		age: "8-14",
		color: "from-green-400 to-emerald-600",
	},
	{
		name: "Roblox",
		icon: "🎮",
		description: "Design and code your own Roblox games",
		age: "9-15",
		color: "from-red-400 to-pink-500",
	},
	{
		name: "Scratch",
		icon: "🧩",
		description: "Learn programming basics through fun, visual blocks",
		age: "7-12",
		color: "from-orange-400 to-pink-500",
	},
	{
		name: "Python",
		icon: "🐍",
		description: "Master the most popular programming language",
		age: "10-16",
		color: "from-blue-400 to-cyan-500",
	},
	{
		name: "JavaScript",
		icon: "⚡",
		description: "Build interactive websites and web applications",
		age: "11-16",
		color: "from-yellow-400 to-orange-500",
	},
	{
		name: "HTML/CSS",
		icon: "🎨",
		description: "Create beautiful websites from scratch",
		age: "9-15",
		color: "from-purple-400 to-pink-500",
	},
	{
		name: "Figma",
		icon: "🎭",
		description: "Learn UI/UX design like a professional",
		age: "12-16",
		color: "from-indigo-400 to-purple-500",
	},
];

const testimonials = [
	{
		parent_name: "Sarah L.",
		parent_initial: "S",
		child_name: "Mia",
		child_age: 10,
		course: "Scratch",
		rating: 5,
		review:
			"ITLand helped Mia fall in love with coding. She now builds mini games on her own!",
	},
	{
		parent_name: "Daniel R.",
		parent_initial: "D",
		child_name: "Leo",
		child_age: 12,
		course: "Python",
		rating: 5,
		review:
			"Leo looks forward to every lesson and proudly shows his Python automation projects.",
	},
	{
		parent_name: "Amira K.",
		parent_initial: "A",
		child_name: "Sophie",
		child_age: 14,
		course: "Figma",
		rating: 5,
		review:
			"Sophie learned design principles and built a portfolio in just six weeks!",
	},
	{
		parent_name: "Michael T.",
		parent_initial: "M",
		child_name: "Ethan",
		child_age: 11,
		course: "Minecraft",
		rating: 5,
		review:
			"Ethan creates amazing Minecraft mods now! The instructor makes coding so fun and engaging.",
	},
	{
		parent_name: "Jennifer W.",
		parent_initial: "J",
		child_name: "Emma",
		child_age: 13,
		course: "JavaScript",
		rating: 5,
		review:
			"Emma built her first interactive website after just 8 weeks. The personalized attention is incredible!",
	},
	{
		parent_name: "Robert C.",
		parent_initial: "R",
		child_name: "Alex",
		child_age: 9,
		course: "Roblox",
		rating: 5,
		review:
			"Alex published his first Roblox game and got 100+ players! The lessons are perfectly paced for kids.",
	},
	{
		parent_name: "Lisa P.",
		parent_initial: "L",
		child_name: "Olivia",
		child_age: 15,
		course: "HTML/CSS",
		rating: 5,
		review:
			"Olivia designed a beautiful portfolio website. The one-on-one sessions really helped her excel!",
	},
	{
		parent_name: "David H.",
		parent_initial: "D",
		child_name: "Noah",
		child_age: 12,
		course: "Python",
		rating: 5,
		review:
			"Noah automated his homework tasks with Python! The instructor explains complex concepts so clearly.",
	},
];

const projects = [
	{
		project_title: "Galaxy Defender",
		image_url: "https://images.unsplash.com/photo-1518770660439-4636190af475",
		student_name: "Aiden",
		age: 11,
		course: "Scratch",
		description:
			"A fast paced arcade shooter with custom animations and power ups.",
		completion_date: "Oct 2025",
	},
	{
		project_title: "Eco Planner",
		image_url: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee",
		student_name: "Lina",
		age: 13,
		course: "Python",
		description:
			"A smart reminder app that suggests eco friendly daily habits.",
		completion_date: "Sep 2025",
	},
	{
		project_title: "Roblox Speedway",
		image_url: "https://images.unsplash.com/photo-1505740106531-4243f3831c78",
		student_name: "Noah",
		age: 12,
		course: "Roblox",
		description:
			"Multiplayer racing experience with custom physics and tracks.",
		completion_date: "Aug 2025",
	},
];

// Utility function to get all trial submissions (for admin/viewing purposes)
export const getTrialSubmissions = async () => {
	if (!isFirebaseConfigured() || !db) {
		// Fallback to localStorage
		try {
			return JSON.parse(localStorage.getItem("trialSubmissions") || "[]");
		} catch (error) {
			console.error("Error retrieving submissions from localStorage:", error);
			return [];
		}
	}

	try {
		const submissionsSnapshot = await getDocs(
			collection(db, "trialSubmissions")
		);
		const submissions = [];
		submissionsSnapshot.forEach((doc) => {
			submissions.push({
				id: doc.id,
				...doc.data(),
			});
		});
		return submissions;
	} catch (error) {
		console.error("Error retrieving submissions from Firebase:", error);
		return [];
	}
};

// Utility function to export submissions as JSON
export const exportSubmissions = async () => {
	try {
		const submissions = await getTrialSubmissions();
		const dataStr = JSON.stringify(submissions, null, 2);
		const dataBlob = new Blob([dataStr], { type: "application/json" });
		const url = URL.createObjectURL(dataBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `trial-submissions-${
			new Date().toISOString().split("T")[0]
		}.json`;
		link.click();
		URL.revokeObjectURL(url);
	} catch (error) {
		console.error("Error exporting submissions:", error);
		alert("Error exporting submissions. Please check the console.");
	}
};

function App() {
	const [showBookingForm, setShowBookingForm] = useState(false);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const [formData, setFormData] = useState({
		parent_name: "",
		parent_email: "",
		parent_phone: "",
		child_name: "",
		child_age: "",
		course: "",
		lesson_type: "group",
		message: "",
	});
	const [formStatus, setFormStatus] = useState({ type: "", message: "" });
	const [trialForm, setTrialForm] = useState({
		parent_name: "",
		child_name: "",
		child_age: "",
		country: "",
		phone: "",
		email: "",
	});
	const [trialStatus, setTrialStatus] = useState({ type: "", message: "" });
	const [showAllCourses, setShowAllCourses] = useState(false);

	const scrollToSection = (id) => {
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
		setMobileNavOpen(false); // Close mobile nav when navigating
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(formData.parent_email)) {
			setFormStatus({
				type: "error",
				message: "Please enter a valid email address.",
			});
			return;
		}

		// Validate phone (basic check)
		if (formData.parent_phone.length < 10) {
			setFormStatus({
				type: "error",
				message: "Please enter a valid phone number.",
			});
			return;
		}

		setFormStatus({ type: "loading", message: "Submitting..." });

		// Check if Firebase is configured
		if (!isFirebaseConfigured() || !db) {
			// Fallback to localStorage
			try {
				const submission = {
					id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
					parent_name: formData.parent_name.trim(),
					parent_email: formData.parent_email.trim().toLowerCase(),
					parent_phone: formData.parent_phone.trim(),
					child_name: formData.child_name.trim(),
					child_age: parseInt(formData.child_age),
					course: formData.course.trim(),
					lesson_type: formData.lesson_type,
					message: formData.message.trim(),
					submitted_at: new Date().toISOString(),
					status: "pending",
					form_type: "modal_booking",
				};

				// Get existing submissions from localStorage
				const existingSubmissions = JSON.parse(
					localStorage.getItem("trialSubmissions") || "[]"
				);

				// Check for duplicate email (within last 24 hours)
				const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
				const recentDuplicate = existingSubmissions.find(
					(sub) =>
						sub.parent_email === submission.parent_email &&
						new Date(sub.submitted_at) > oneDayAgo
				);

				if (recentDuplicate) {
					setFormStatus({
						type: "error",
						message:
							"You've already submitted a booking request recently. Please wait 24 hours or contact us directly.",
					});
					return;
				}

				// Add new submission
				existingSubmissions.push(submission);
				localStorage.setItem(
					"trialSubmissions",
					JSON.stringify(existingSubmissions)
				);

				setFormStatus({
					type: "success",
					message: `🎉 Thank you ${formData.parent_name}! Your booking has been submitted successfully. We'll contact you at ${formData.parent_email} within 24 hours!`,
				});

				console.log("Booking submission saved to localStorage:", submission);

				setFormData({
					parent_name: "",
					parent_email: "",
					parent_phone: "",
					child_name: "",
					child_age: "",
					course: "",
					lesson_type: "group",
					message: "",
				});

				setTimeout(() => {
					setShowBookingForm(false);
					setFormStatus({ type: "", message: "" });
				}, 3000);
			} catch (error) {
				console.error("Error saving to localStorage:", error);
				setFormStatus({
					type: "error",
					message:
						"Sorry, there was an error submitting your request. Please try again or contact us directly.",
				});
			}
			return;
		}

		// Use Firebase if configured
		try {
			const emailLower = formData.parent_email.trim().toLowerCase();

			// Check for duplicate email (within last 24 hours)
			const oneDayAgo = Timestamp.fromDate(
				new Date(Date.now() - 24 * 60 * 60 * 1000)
			);

			const duplicateQuery = query(
				collection(db, "trialSubmissions"),
				where("parent_email", "==", emailLower),
				where("submitted_at", ">", oneDayAgo)
			);

			const duplicateSnapshot = await getDocs(duplicateQuery);

			if (!duplicateSnapshot.empty) {
				setFormStatus({
					type: "error",
					message:
						"You've already submitted a booking request recently. Please wait 24 hours or contact us directly.",
				});
				return;
			}

			// Create submission object
			const submission = {
				parent_name: formData.parent_name.trim(),
				parent_email: emailLower,
				parent_phone: formData.parent_phone.trim(),
				child_name: formData.child_name.trim(),
				child_age: parseInt(formData.child_age),
				course: formData.course.trim(),
				lesson_type: formData.lesson_type,
				message: formData.message.trim(),
				submitted_at: Timestamp.now(),
				status: "pending",
				form_type: "modal_booking",
			};

			// Save to Firestore
			const docRef = await addDoc(
				collection(db, "trialSubmissions"),
				submission
			);

			// Success feedback
			setFormStatus({
				type: "success",
				message: `🎉 Thank you ${formData.parent_name}! Your booking has been submitted successfully. We'll contact you at ${formData.parent_email} within 24 hours!`,
			});

			// Log submission for debugging
			console.log("Booking submission saved to Firebase:", {
				id: docRef.id,
				parent: submission.parent_name,
				child: submission.child_name,
				email: submission.parent_email,
				course: submission.course,
			});

			// Reset form
			setFormData({
				parent_name: "",
				parent_email: "",
				parent_phone: "",
				child_name: "",
				child_age: "",
				course: "",
				lesson_type: "group",
				message: "",
			});

			// Close modal after 3 seconds
			setTimeout(() => {
				setShowBookingForm(false);
				setFormStatus({ type: "", message: "" });
			}, 3000);
		} catch (error) {
			console.error("Error submitting booking to Firebase:", error);
			setFormStatus({
				type: "error",
				message:
					"Sorry, there was an error submitting your request. Please try again or contact us directly.",
			});
		}
	};

	const handleTrialChange = (e) => {
		const { name, value } = e.target;
		setTrialForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleTrialSubmit = async (e) => {
		e.preventDefault();

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(trialForm.email)) {
			setTrialStatus({
				type: "error",
				message: "Please enter a valid email address.",
			});
			return;
		}

		// Validate phone (basic check)
		if (trialForm.phone.length < 10) {
			setTrialStatus({
				type: "error",
				message: "Please enter a valid phone number.",
			});
			return;
		}

		setTrialStatus({ type: "loading", message: "Scheduling your class..." });

		// Check if Firebase is configured
		if (!isFirebaseConfigured() || !db) {
			// Fallback to localStorage
			try {
				const submission = {
					id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
					parent_name: trialForm.parent_name.trim(),
					child_name: trialForm.child_name.trim(),
					child_age: parseInt(trialForm.child_age),
					country: trialForm.country.trim(),
					phone: trialForm.phone.trim(),
					email: trialForm.email.trim().toLowerCase(),
					submitted_at: new Date().toISOString(),
					status: "pending",
				};

				// Get existing submissions from localStorage
				const existingSubmissions = JSON.parse(
					localStorage.getItem("trialSubmissions") || "[]"
				);

				// Check for duplicate email (within last 24 hours)
				const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
				const recentDuplicate = existingSubmissions.find(
					(sub) =>
						sub.email === submission.email &&
						new Date(sub.submitted_at) > oneDayAgo
				);

				if (recentDuplicate) {
					setTrialStatus({
						type: "error",
						message:
							"You've already submitted a trial request recently. Please wait 24 hours or contact us directly.",
					});
					return;
				}

				// Add new submission
				existingSubmissions.push(submission);
				localStorage.setItem(
					"trialSubmissions",
					JSON.stringify(existingSubmissions)
				);

				setTrialStatus({
					type: "success",
					message: `🎉 Thank you ${trialForm.parent_name}! Your free trial request has been submitted successfully. We'll contact you at ${trialForm.email} within one business day.`,
				});

				console.log("Trial submission saved to localStorage:", submission);

				setTrialForm({
					parent_name: "",
					child_name: "",
					child_age: "",
					country: "",
					phone: "",
					email: "",
				});

				setTimeout(() => setTrialStatus({ type: "", message: "" }), 5000);
			} catch (error) {
				console.error("Error saving to localStorage:", error);
				setTrialStatus({
					type: "error",
					message:
						"Sorry, there was an error submitting your request. Please try again or contact us directly.",
				});
			}
			return;
		}

		// Use Firebase if configured
		try {
			// Check for duplicate email (within last 24 hours)
			const oneDayAgo = Timestamp.fromDate(
				new Date(Date.now() - 24 * 60 * 60 * 1000)
			);
			const emailLower = trialForm.email.trim().toLowerCase();

			const duplicateQuery = query(
				collection(db, "trialSubmissions"),
				where("email", "==", emailLower),
				where("submitted_at", ">", oneDayAgo)
			);

			const duplicateSnapshot = await getDocs(duplicateQuery);

			if (!duplicateSnapshot.empty) {
				setTrialStatus({
					type: "error",
					message:
						"You've already submitted a trial request recently. Please wait 24 hours or contact us directly.",
				});
				return;
			}

			// Create submission object
			const submission = {
				parent_name: trialForm.parent_name.trim(),
				child_name: trialForm.child_name.trim(),
				child_age: parseInt(trialForm.child_age),
				country: trialForm.country.trim(),
				phone: trialForm.phone.trim(),
				email: emailLower,
				submitted_at: Timestamp.now(),
				status: "pending",
			};

			// Save to Firestore
			const docRef = await addDoc(
				collection(db, "trialSubmissions"),
				submission
			);

			// Success feedback
			setTrialStatus({
				type: "success",
				message: `🎉 Thank you ${trialForm.parent_name}! Your free trial request has been submitted successfully. We'll contact you at ${trialForm.email} within one business day.`,
			});

			// Log submission for debugging
			console.log("Trial submission saved to Firebase:", {
				id: docRef.id,
				parent: submission.parent_name,
				child: submission.child_name,
				email: submission.email,
			});

			// Reset form
			setTrialForm({
				parent_name: "",
				child_name: "",
				child_age: "",
				country: "",
				phone: "",
				email: "",
			});

			// Clear status message after 5 seconds
			setTimeout(() => setTrialStatus({ type: "", message: "" }), 5000);
		} catch (error) {
			console.error("Error submitting trial request to Firebase:", error);
			setTrialStatus({
				type: "error",
				message:
					"Sorry, there was an error submitting your request. Please try again or contact us directly.",
			});
		}
	};

	return (
		<div className='app'>
			<nav className='nav'>
				<div className='nav-container'>
					<div className='nav-logo' onClick={() => scrollToSection("top")}>
						<span className='logo-icon'>🚀</span>
						<span className='logo-text'>ITLand</span>
					</div>
					<button
						className={`mobile-nav-toggle ${mobileNavOpen ? "active" : ""}`}
						onClick={() => setMobileNavOpen(!mobileNavOpen)}
						aria-label='Toggle navigation menu'
					>
						<span></span>
						<span></span>
						<span></span>
					</button>
					<div className={`nav-links ${mobileNavOpen ? "active" : ""}`}>
						<button
							onClick={() => scrollToSection("courses")}
							className='nav-link'
						>
							Courses
						</button>
						<button
							onClick={() => scrollToSection("about")}
							className='nav-link'
						>
							About
						</button>
						<button
							onClick={() => scrollToSection("learning-options")}
							className='nav-link'
						>
							Learning Options
						</button>
						<button
							onClick={() => scrollToSection("projects")}
							className='nav-link'
						>
							Projects
						</button>
						<button
							onClick={() => scrollToSection("reviews")}
							className='nav-link'
						>
							Reviews
						</button>
						<a href='https://booking-itland.netlify.app' className='nav-cta'>
							Book Free Trial
						</a>
					</div>
				</div>
			</nav>

			<section id='top' className='hero'>
				<div className='hero-bg'>
					<div className='cube cube-1'></div>
					<div className='cube cube-2'></div>
					<div className='cube cube-3'></div>
					<div className='cube cube-4'></div>
				</div>

				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>

				{/* Multiple shooting star layers with different colors and speeds */}
				<ShadcnShootingStars
					starColor='#fbbf24'
					trailColor='#f59e0b'
					minSpeed={6}
					maxSpeed={16}
					minDelay={1000}
					maxDelay={3500}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#667eea'
					trailColor='#764ba2'
					minSpeed={7}
					maxSpeed={17}
					minDelay={1500}
					maxDelay={4000}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#10b981'
					trailColor='#059669'
					minSpeed={5}
					maxSpeed={14}
					minDelay={2000}
					maxDelay={4500}
					starWidth={15}
					starHeight={2}
				/>

				<div className='hero-content'>
					<h1 className='hero-title'>
						<span className='hero-title-main'>Learn to Code</span>
						<span className='hero-title-sub'>Build Amazing Things! 🎮</span>
					</h1>
					<div className='hero-badge'>
						<span className='badge-icon'>🌐</span>
						<span className='badge-text'>
							Online Programming School for Kids
						</span>
					</div>
					<p className='hero-description'>
						Join ITLand - the premier <strong>online programming school</strong>{" "}
						where kids learn Python, Scratch, Minecraft, Roblox, and more from
						the comfort of home!
					</p>
					<div className='hero-stats'>
						<div className='stat-box'>
							<div className='stat-number'>100+</div>
							<div className='stat-label'>Happy Students</div>
						</div>
						<div className='stat-box'>
							<div className='stat-number'>7</div>
							<div className='stat-label'>Courses</div>
						</div>
						<div className='stat-box'>
							<div className='stat-number'>1</div>
							<div className='stat-label'>Expert Teacher</div>
						</div>
					</div>
					<button onClick={() => setShowBookingForm(true)} className='hero-cta'>
						🎁 Start Free Trial
					</button>
				</div>
			</section>

			<section id='about' className='about-section'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<div className='container'>
					<h2 className='section-title'>
						<span className='title-icon'>👨‍💻</span>
						Meet Your Instructor
					</h2>
					<p
						style={{
							textAlign: "center",
							fontSize: "1.2rem",
							marginBottom: "2rem",
							fontWeight: 600,
						}}
					>
						Jasurbek Sadiev - CEO & Instructor
					</p>
					<div className='about-content'>
						<div className='about-video'>
							<div className='video-placeholder'>
								<div className='play-button'>▶</div>
								<p>Instructor Introduction Video</p>
							</div>
						</div>
						<div className='about-text'>
							<h3>Why Choose ITLand?</h3>
							<p className='about-highlight'>
								ITLand is a <strong>100% online programming school</strong>{" "}
								designed specifically for kids. Learn from anywhere in the world
								with live interactive sessions. Unlike other schools with
								multiple teachers, I personally teach ALL courses. This means
								consistent quality, personalized attention, and guaranteed
								results for your child.
							</p>
							<div className='credentials'>
								<div className='credential'>
									<span className='credential-icon'>🏆</span>
									<div>
										<h4>IBM Certified</h4>
										<p>JavaScript Backend Developer</p>
									</div>
								</div>
								<div className='credential'>
									<span className='credential-icon'>🎓</span>
									<div>
										<h4>Bachelor's Degree</h4>
										<p>Computer Science</p>
									</div>
								</div>
								<div className='credential'>
									<span className='credential-icon'>💼</span>
									<div>
										<h4>5+ Years</h4>
										<p>Teaching Experience</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section id='courses' className='courses-section'>
				{/* Background with stars */}
				<div className='courses-bg-overlay'>
					<div className='courses-radial-gradient' />
					<div className='shooting-stars-bg' />
				</div>

				{/* Multiple shooting star layers with different colors and speeds */}
				<ShadcnShootingStars
					starColor='#9E00FF'
					trailColor='#2EB9DF'
					minSpeed={7}
					maxSpeed={17}
					minDelay={1200}
					maxDelay={3000}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#FF0099'
					trailColor='#FFB800'
					minSpeed={5}
					maxSpeed={12}
					minDelay={2000}
					maxDelay={4000}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#00FF9E'
					trailColor='#00B8FF'
					minSpeed={10}
					maxSpeed={20}
					minDelay={1500}
					maxDelay={3500}
					starWidth={15}
					starHeight={2}
				/>

				<div className='container courses-content'>
					<h2 className='section-title'>
						<span className='title-icon'>🎯</span>
						Our Online Courses
					</h2>
					<p className='section-subtitle'>
						Choose your coding adventure! All courses are taught live online.
					</p>
					<div className='courses-grid'>
						{(showAllCourses ? courses : courses.slice(0, 4)).map(
							(course, index) => (
								<div
									key={index}
									className='course-card'
									style={{ animationDelay: `${index * 0.1}s` }}
								>
									{getCourseImage(course.name) ? (
										<img
											src={getCourseImage(course.name)}
											alt={course.name}
											className='course-image'
										/>
									) : (
										<div
											className={`course-gradient bg-gradient-to-br ${course.color}`}
										></div>
									)}
									<div className='course-content'>
										<div className='course-icon'>{course.icon}</div>
										<h3>{course.name}</h3>
										<p className='course-age'>Ages {course.age}</p>
										<p className='course-description'>{course.description}</p>
										<button
											onClick={() => {
												setFormData((prev) => ({
													...prev,
													course: course.name,
												}));
												setShowBookingForm(true);
											}}
											className='course-btn'
										>
											Start Learning
										</button>
									</div>
								</div>
							)
						)}
					</div>
					{!showAllCourses && courses.length > 4 && (
						<div style={{ textAlign: "center", marginTop: "3rem" }}>
							<button
								onClick={() => setShowAllCourses(true)}
								className='hero-cta'
								style={{ fontSize: "1.2rem", padding: "1rem 2.5rem" }}
							>
								Show All Courses
							</button>
						</div>
					)}
				</div>
			</section>

			<section id='learning-options' className='learning-options-page'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<section className='learning-hero'>
					<div className='floating-blocks'>
						<div className='minecraft-block block-1'></div>
						<div className='minecraft-block block-2'></div>
						<div className='minecraft-block block-3'></div>
						<div className='minecraft-block block-4'></div>
						<div className='minecraft-block block-5'></div>
						<div className='minecraft-block block-6'></div>
					</div>
					<div className='container'>
						<h1 className='learning-title'>
							<span className='title-emoji'>🎮</span>
							Choose Your Learning Adventure
							<span className='title-emoji'>🎯</span>
						</h1>
						<p className='learning-subtitle'>
							Pick the perfect learning style for your coding journey!
						</p>
					</div>
				</section>

				<section className='options-section'>
					<div className='container'>
						<div className='options-inner'>
							<div className='options-grid'>
								<div className='option-card group-option'>
									{/* <div className='option-3d-scene'>
										<div className='group-icon-3d'>
											<div className='student-avatar avatar-1'>👧</div>
											<div className='student-avatar avatar-2'>👦</div>
											<div className='student-avatar avatar-3'>👧</div>
											<div className='student-avatar avatar-4'>👦</div>
										</div>
									</div> */}
									<div className='option-content'>
										<div className='option-badge'>Most Popular</div>
										<h2 className='option-title'>
											<span className='option-icon'>👥</span>
											Group Lessons
										</h2>
										<p className='option-description'>
											Learn together with friends and make coding fun!
										</p>

										<div className='option-details'>
											<div className='detail-item'>
												<span className='detail-icon'>👥</span>
												<div>
													<strong>Class Size</strong>
													<p>Up to 4 students per group</p>
												</div>
											</div>

											<div className='detail-item'>
												<span className='detail-icon'>📅</span>
												<div>
													<strong>Schedule</strong>
													<p>Once a week</p>
												</div>
											</div>

											<div className='detail-item'>
												<span className='detail-icon'>⏱️</span>
												<div>
													<strong>Duration</strong>
													<p>60-90 minutes per lesson</p>
												</div>
											</div>

											{/* <div className='detail-item'>
												<span className='detail-icon'>💬</span>
												<div>
													<strong>Support</strong>
													<p>Tutor support anytime</p>
												</div>
											</div> */}
										</div>

										{/* <div className='skills-section'>
											<h3>💪 Skills You'll Gain</h3>
											<div className='skills-tags'>
												<span className='skill-tag'>🤝 Teamwork</span>
												<span className='skill-tag'>🗣️ Social Skills</span>
												<span className='skill-tag'>👫 Collaboration</span>
												<span className='skill-tag'>
													🎯 Problem Solving Together
												</span>
												<span className='skill-tag'>🏆 Peer Learning</span>
											</div>
										</div> */}

										{/* <button
											className='option-cta group-cta'
											onClick={() => setShowBookingForm(true)}
										>
											Join a Group 🎉
										</button> */}
									</div>
								</div>

								<div className='option-card individual-option'>
									{/* <div className='option-3d-scene'>
										<div className='individual-icon-3d'>
											<div className='student-single'>
												<div className='student-face'>🧒</div>
												<div className='focus-rings'>
													<div className='ring ring-1'></div>
													<div className='ring ring-2'></div>
													<div className='ring ring-3'></div>
												</div>
											</div>
										</div>
									</div> */}
									<div className='option-content'>
										<div className='option-badge premium'>Premium Choice</div>
										<h2 className='option-title'>
											<span className='option-icon'>🎯</span>
											One-on-One Lessons
										</h2>
										<p className='option-description'>
											Personalized attention for maximum growth!
										</p>

										<div className='option-details'>
											<div className='detail-item'>
												<span className='detail-icon'>👤</span>
												<div>
													<strong>Class Size</strong>
													<p>Just you and the tutor</p>
												</div>
											</div>

											<div className='detail-item'>
												<span className='detail-icon'>📅</span>
												<div>
													<strong>Schedule</strong>
													<p>1, 2, or 3 times a week</p>
												</div>
											</div>

											<div className='detail-item'>
												<span className='detail-icon'>⏱️</span>
												<div>
													<strong>Duration</strong>
													<p>50 minutes per lesson</p>
												</div>
											</div>

											{/* <div className='detail-item'>
												<span className='detail-icon'>📋</span>
												<div>
													<strong>Learning Plan</strong>
													<p>Custom plan just for you</p>
												</div>
											</div> */}
										</div>

										{/* <div className='skills-section'>
											<h3>💪 Skills You'll Gain</h3>
											<div className='skills-tags'>
												<span className='skill-tag'>🎯 Focused Learning</span>
												<span className='skill-tag'>🚀 Faster Progress</span>
												<span className='skill-tag'>🧠 Deep Understanding</span>
												<span className='skill-tag'>💡 Creative Thinking</span>
												<span className='skill-tag'>🏅 Personalized Goals</span>
												<span className='skill-tag'>⚡ Advanced Skills</span>
											</div>
										</div>

										<button
											className='option-cta individual-cta'
											onClick={() => setShowBookingForm(true)}
										>
											Get Personal Training 🌟
										</button> */}
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</section>

			<section id='projects' className='projects-section'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<div className='container'>
					<h2 className='section-title'>
						<span className='title-icon'>🌟</span>
						Student Success Stories
					</h2>
					<p className='section-subtitle'>
						Amazing projects created by our students!
					</p>
					<div className='projects-grid'>
						{projects.map((project, index) => (
							<div key={index} className='project-window'>
								<div className='window-header'>
									<div className='window-buttons'>
										<span className='window-btn red'></span>
										<span className='window-btn yellow'></span>
										<span className='window-btn green'></span>
									</div>
									<div className='window-title'>{project.project_title}</div>
								</div>
								<div className='window-content'>
									<img src={project.image_url} alt={project.project_title} />
									<div className='project-info'>
										<h4>{project.project_title}</h4>
										<p className='project-meta'>
											{project.student_name}, {project.age} years •{" "}
											{project.course}
										</p>
										<p className='project-description'>{project.description}</p>
										<p className='project-date'>
											Completed: {project.completion_date}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section id='reviews' className='testimonials-section'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<div className='container'>
					<h2 className='section-title'>
						<span className='title-icon'>💬</span>
						What Parents Say
					</h2>
					<div className='testimonials-slider-wrapper'>
						<div className='testimonials-slider'>
							{/* First set of testimonials */}
							{testimonials.map((testimonial, index) => (
								<div key={`first-${index}`} className='testimonial-card'>
									<div className='testimonial-stars'>
										{"⭐".repeat(testimonial.rating)}
									</div>
									<p className='testimonial-text'>"{testimonial.review}"</p>
									<div className='testimonial-author'>
										<div className='author-avatar'>
											{testimonial.parent_initial}
										</div>
										<div>
											<p className='author-name'>{testimonial.parent_name}</p>
											<p className='author-meta'>
												Parent of {testimonial.child_name},{" "}
												{testimonial.child_age} • {testimonial.course}
											</p>
										</div>
									</div>
								</div>
							))}
							{/* Duplicate set for seamless loop */}
							{testimonials.map((testimonial, index) => (
								<div key={`second-${index}`} className='testimonial-card'>
									<div className='testimonial-stars'>
										{"⭐".repeat(testimonial.rating)}
									</div>
									<p className='testimonial-text'>"{testimonial.review}"</p>
									<div className='testimonial-author'>
										<div className='author-avatar'>
											{testimonial.parent_initial}
										</div>
										<div>
											<p className='author-name'>{testimonial.parent_name}</p>
											<p className='author-meta'>
												Parent of {testimonial.child_name},{" "}
												{testimonial.child_age} • {testimonial.course}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			<section id='signup' className='signup-section'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<BackgroundBoxes className='signup-background-boxes' />
				<ShadcnShootingStars
					starColor='#9E00FF'
					trailColor='#2EB9DF'
					minSpeed={5}
					maxSpeed={15}
					minDelay={1200}
					maxDelay={4200}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#FF0099'
					trailColor='#FFB800'
					minSpeed={7}
					maxSpeed={17}
					minDelay={2000}
					maxDelay={4000}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#00FF9E'
					trailColor='#00B8FF'
					minSpeed={6}
					maxSpeed={14}
					minDelay={1500}
					maxDelay={3500}
					starWidth={15}
					starHeight={2}
				/>
				<div className='container'>
					<div className='signup-card'>
						<h2 className='signup-title'>Sign Up for Free Trial Class</h2>
						<p className='signup-badge'>Free 55-minute trial class</p>
						<form
							className='booking-form signup-form'
							onSubmit={handleTrialSubmit}
						>
							<div className='form-row'>
								<div className='form-group'>
									<label>Parent Name *</label>
									<input
										type='text'
										name='parent_name'
										value={trialForm.parent_name}
										onChange={handleTrialChange}
										required
										placeholder='Jane Doe'
									/>
								</div>
								<div className='form-group'>
									<label>Child Name *</label>
									<input
										type='text'
										name='child_name'
										value={trialForm.child_name}
										onChange={handleTrialChange}
										required
										placeholder='Ava Doe'
									/>
								</div>
							</div>

							<div className='form-row'>
								<div className='form-group'>
									<label>Child Age *</label>
									<input
										type='number'
										name='child_age'
										value={trialForm.child_age}
										onChange={handleTrialChange}
										required
										min='6'
										max='18'
										placeholder='11'
									/>
								</div>
								<div className='form-group'>
									<label>Country *</label>
									<input
										type='text'
										name='country'
										value={trialForm.country}
										onChange={handleTrialChange}
										required
										placeholder='United States'
									/>
								</div>
							</div>

							<div className='form-row'>
								<div className='form-group'>
									<label>Phone Number *</label>
									<input
										type='tel'
										name='phone'
										value={trialForm.phone}
										onChange={handleTrialChange}
										required
										placeholder='+1 (555) 123-4567'
									/>
								</div>
								<div className='form-group'>
									<label>Email Address *</label>
									<input
										type='email'
										name='email'
										value={trialForm.email}
										onChange={handleTrialChange}
										required
										placeholder='parent@email.com'
									/>
								</div>
							</div>

							{trialStatus.message && (
								<div className={`form-status ${trialStatus.type}`}>
									{trialStatus.message}
								</div>
							)}

							<button
								type='submit'
								className='submit-btn'
								disabled={trialStatus.type === "loading"}
							>
								{trialStatus.type === "loading"
									? "Booking..."
									: "🚀 Claim Free Trial"}
							</button>
						</form>
					</div>
				</div>
			</section>

			{showBookingForm && (
				<div
					className='modal-overlay'
					onClick={() => setShowBookingForm(false)}
				>
					<div className='modal-content' onClick={(e) => e.stopPropagation()}>
						<button
							className='modal-close'
							onClick={() => setShowBookingForm(false)}
						>
							✕
						</button>
						<h2 className='modal-title'>🎉 Book Your Free Trial</h2>
						<p className='modal-subtitle'>Start your coding journey today!</p>

						{formStatus.message && (
							<div className={`form-status ${formStatus.type}`}>
								{formStatus.message}
							</div>
						)}

						<form onSubmit={handleSubmit} className='booking-form'>
							<div className='form-row'>
								<div className='form-group'>
									<label>Parent Name *</label>
									<input
										type='text'
										name='parent_name'
										value={formData.parent_name}
										onChange={handleInputChange}
										required
										placeholder='Your full name'
									/>
								</div>
								<div className='form-group'>
									<label>Parent Email *</label>
									<input
										type='email'
										name='parent_email'
										value={formData.parent_email}
										onChange={handleInputChange}
										required
										placeholder='your@email.com'
									/>
								</div>
							</div>

							<div className='form-group'>
								<label>Phone Number *</label>
								<input
									type='tel'
									name='parent_phone'
									value={formData.parent_phone}
									onChange={handleInputChange}
									required
									placeholder='+1 (555) 123-4567'
								/>
							</div>

							<div className='form-row'>
								<div className='form-group'>
									<label>Child's Name *</label>
									<input
										type='text'
										name='child_name'
										value={formData.child_name}
										onChange={handleInputChange}
										required
										placeholder="Child's full name"
									/>
								</div>
								<div className='form-group'>
									<label>Child's Age *</label>
									<input
										type='number'
										name='child_age'
										value={formData.child_age}
										onChange={handleInputChange}
										required
										min='6'
										max='18'
										placeholder='Age'
									/>
								</div>
							</div>

							<div className='form-group'>
								<label>Course *</label>
								<select
									name='course'
									value={formData.course}
									onChange={handleInputChange}
									required
								>
									<option value=''>Select a course</option>
									{courses.map((course) => (
										<option key={course.name} value={course.name}>
											{course.name}
										</option>
									))}
								</select>
							</div>

							<div className='form-group'>
								<label>Additional Message (Optional)</label>
								<textarea
									name='message'
									value={formData.message}
									onChange={handleInputChange}
									rows='3'
									placeholder='Any questions or special requests?'
								></textarea>
							</div>

							<button
								type='submit'
								className='submit-btn'
								disabled={formStatus.type === "loading"}
							>
								{formStatus.type === "loading"
									? "Submitting..."
									: "🚀 Book Free Trial"}
							</button>
						</form>
					</div>
				</div>
			)}

			<footer className='footer'>
				{/* Twinkling stars background */}
				<div className='section-bg-overlay'>
					<div className='stars-bg' />
				</div>
				<ShadcnShootingStars
					starColor='#667eea'
					trailColor='#764ba2'
					minSpeed={4}
					maxSpeed={12}
					minDelay={1500}
					maxDelay={4500}
					starWidth={15}
					starHeight={2}
				/>
				<ShadcnShootingStars
					starColor='#10b981'
					trailColor='#059669'
					minSpeed={5}
					maxSpeed={15}
					minDelay={2000}
					maxDelay={5000}
					starWidth={15}
					starHeight={2}
				/>
				<div className='container'>
					<div className='footer-content'>
						<div className='footer-section'>
							<h3>🚀 ITLand</h3>
							<p>Online Programming School for Kids</p>
							<p
								style={{
									fontSize: "0.9rem",
									opacity: 0.8,
									marginTop: "0.5rem",
								}}
							>
								Where kids learn to code and create amazing things from anywhere
								in the world!
							</p>
						</div>
						<div className='footer-section'>
							<h4>Contact</h4>
							<p>📧 itland.uk@gmail.com</p>
							<p>📱 +447446959618</p>
							<p>🌐 Online Lessons Worldwide</p>
						</div>
						<div className='footer-section'>
							<h4>Quick Links</h4>
							<button
								onClick={() => scrollToSection("courses")}
								className='footer-link'
							>
								Courses
							</button>
							<button
								onClick={() => scrollToSection("about")}
								className='footer-link'
							>
								About
							</button>
							<button
								onClick={() => scrollToSection("projects")}
								className='footer-link'
							>
								Projects
							</button>
							<button
								onClick={() => setShowBookingForm(true)}
								className='footer-link'
							>
								Book Trial
							</button>
						</div>
					</div>
					<div className='footer-bottom'>
						<p>
							© 2025 ITLand. All rights reserved. Made with ❤️ for young coders!
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}

export default App;
